import React, { Fragment, useMemo, useState } from "react";
import { Link, NavLink, useHistory, useLocation } from "react-router-dom";
import {
  Row,
  Col,
  Drawer,
  Button,
  Dropdown,
  Menu,
  Avatar,
  Divider,
  Space,
} from "antd";
import { DownOutlined, UserOutlined } from "@ant-design/icons";
import SearchBar from "./AdvancedSearchNav";
import NoCapManifest from "../../nocap.json";
import "./TopNavBar.css";
import "./MobileSearch.css";
import {
  InstagramOutlined,
  FacebookOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";

// --------- localStorage safe helper (prevents random blank screens) ----------
const safeJSONParse = (value, fallback = null) => {
  try {
    if (!value || value === "undefined" || value === "null") return fallback;
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const TopNavBar = (props) => {
  const [drawer, setDrawer] = useState(false);
  const { isAuthenticated } = props.auth;
  const location = useLocation();

  const history = useHistory();
  const handleLogout = () => {
    props.auth.logout();
    history.push("/");
  };
  const handleDrawerClose = () => {
    setDrawer(false);
  };

  const socialButtonStyle = {
    borderColor: "#bfbfbf",
    width: 30,
    height: 30,
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 30,
  };

  // ✅ safe user read (won’t crash if JSON is corrupted in some browsers/devices)
  const user = useMemo(
    () => safeJSONParse(localStorage.getItem("user"), null),
    []
  );
  const authed = Boolean(isAuthenticated?.() && user);

  // Home navigation links for mobile
  const homeNavLinks = [
    { title: "Elections", path: "/mobileElections" },
    { title: "Politician(s)", path: "/mobilePoliticians" },
    { title: "Coalitions", path: "/mobileCoalitions" },
    { title: "Organizations", path: "/mobileOrganizations" },
    { title: "Movements", path: "/mobileMovements" },
    { title: "Services", path: "/mobileServices" },
  ];

  // Desktop
  let accountLinks = (
    <Space size="small">
      <NavLink to="/signup" style={{ fontWeight: "bold" }}>
        SIGN&nbsp;UP
      </NavLink>
      <Divider type="vertical" style={{ backgroundColor: "darkgrey" }} />
      <NavLink to="/login" style={{ fontWeight: "bold" }}>
        LOGIN
      </NavLink>
    </Space>
  );

  let menuLinks = NoCapManifest.pages.map((item, i) => (
    <NavLink key={item.path + i} to={item.path}>
      {item.title}
    </NavLink>
  ));

  // Mobile
  let mobileAccountLinks = (
    <Space direction="vertical" align="center">
      <NavLink
        onClick={handleDrawerClose}
        to="/signup"
        style={{ fontWeight: "bold", fontSize: "1rem" }}
      >
        SIGN&nbsp;UP
      </NavLink>
      <NavLink
        onClick={handleDrawerClose}
        to="/login"
        style={{ fontWeight: "bold", fontSize: "1rem" }}
      >
        LOGIN
      </NavLink>
    </Space>
  );

  let mobileMenuLinks = NoCapManifest.pages.map((item, i) => (
    <NavLink
      key={item.path + i}
      to={item.path}
      onClick={handleDrawerClose}
    >
      {item.title}
    </NavLink>
  ));

  // Mobile home navigation links
  let mobileHomeNavLinks = homeNavLinks.map((item, i) => (
    <NavLink
      key={item.path + i}
      to={item.path}
      onClick={handleDrawerClose}
      style={{
        fontWeight: location.pathname === item.path ? "bold" : "normal",
      }}
    >
      {item.title}
    </NavLink>
  ));

  if (authed) {
    const username = user?.nickname ? user.nickname : user?.name || "User";
    const role = user?.role || "user";

    const roleMenuItems =
      user?.email_verified && NoCapManifest?.roles?.[role]?.topNavMenuItems
        ? NoCapManifest.roles[role].topNavMenuItems
        : [];

    const dropDownMenu = (
      <Menu>
        {roleMenuItems.map((item) => (
          <Menu.Item key={item.path}>
            <NavLink to={item.path}>{item.name}</NavLink>
          </Menu.Item>
        ))}

        {role !== "admin" && (
          <Menu.Item key="change-type">
            <NavLink to="/change-type">Change account type</NavLink>
          </Menu.Item>
        )}

        <Menu.Item key="logout">
          <NavLink to="/" onClick={handleLogout}>
            Log&nbsp;out
          </NavLink>
        </Menu.Item>
      </Menu>
    );

    accountLinks = (
      <Space size="small">
        <Divider type="vertical" style={{ backgroundColor: "darkgrey" }} />
        Welcome,
        <Dropdown overlay={dropDownMenu}>
          <a
            className="ant-dropdown-link"
            href="/#"
            onClick={(e) => e.preventDefault()}
          >
            {username} <DownOutlined />
          </a>
        </Dropdown>
      </Space>
    );

    const picture = user?.picture || "";
    const userPicture =
      picture &&
      !picture.startsWith("https://s.gravatar.com/avatar/") ? (
        <Avatar src={picture} />
      ) : (
        <Avatar icon={<UserOutlined />} />
      );

    mobileAccountLinks = (
      <Fragment>
        <Space>
          {userPicture} <span className="mobile-username">{username}</span>
        </Space>

        {roleMenuItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={handleDrawerClose}
          >
            {item.name}
          </NavLink>
        ))}

        <NavLink to="/" onClick={handleLogout}>
          Log&nbsp;out
        </NavLink>
      </Fragment>
    );
  }

  const menuIcon = (
    <Button id="hamburger" onClick={() => setDrawer(true)}>
      <div className="line" />
      <div className="line" />
      <div className="line" />
    </Button>
  );

  return (
    <Row align="middle" justify="center" className="top-nav-bar">
      <Col xs={{ span: 18, order: 1 }} lg={{ span: 6, order: 1 }} align="left">
        <Link to="/">
          <img
            src="https://s3.amazonaws.com/dev.nocap.com/uploads/logo.png"
            alt="NoCap Logo"
            className="nav-logo"
          />
        </Link>
      </Col>

      <Col className="search-bar" xs={{ span: 0, order: 3 }} lg={{ span: 6, order: 3 }}>
        <SearchBar />
      </Col>

      <Col xs={{ span: 6, order: 2 }} lg={{ span: 0, order: 2 }} align="right">
        {menuIcon}
      </Col>

      <Drawer
        placement="right"
        closable
        onClose={() => setDrawer(false)}
        visible={drawer}
        width={240}
        drawerStyle={{
          alignItems: "center",
          justifyContent: "flex-start",
          paddingTop: "20px",
        }}
      >
        <Space
          size="medium"
          direction="vertical"
          align="center"
          style={{ width: "100%" }}
        >
          {mobileAccountLinks}

          <Divider style={{ margin: "20px 0", width: "90%" }} />

          <div className="mobile-search-wrapper">
            <SearchBar />
          </div>

          <Divider style={{ margin: "20px 0", width: "90%" }} />

          {mobileHomeNavLinks}

          <Divider style={{ margin: "20px 0", width: "90%" }} />

          {mobileMenuLinks}

          <Divider style={{ margin: "20px 0", width: "90%" }} />

          <p>Follow NO CAP</p>

          {/* ✅ Use a div instead of Col inside Drawer content */}
          <div className="flex-row" style={{ display: "flex", gap: 8 }}>
            <Button
              style={socialButtonStyle}
              className="btn-bordered"
              href="https://www.instagram.com/nocapvote/"
              target="_blank"
              rel="noopener noreferrer"
              icon={<InstagramOutlined style={{ fontSize: 14 }} />}
            />
            <Button
              style={socialButtonStyle}
              className="btn-bordered"
              href="https://x.com/nocapvote"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </Button>
            <Button
              style={socialButtonStyle}
              className="btn-bordered"
              href="https://www.tiktok.com/@nocapvote"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
              </svg>
            </Button>
            <Button
              style={socialButtonStyle}
              className="btn-bordered"
              href="https://www.facebook.com/nocapvote"
              target="_blank"
              rel="noopener noreferrer"
              icon={<FacebookOutlined style={{ fontSize: 14 }} />}
            />
            <Button
              style={socialButtonStyle}
              className="btn-bordered"
              href="https://bsky.app/profile/nocapvote.bsky.social"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.038.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.018.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z" />
              </svg>
            </Button>
            <Button
              style={socialButtonStyle}
              className="btn-bordered"
              href="https://www.youtube.com/@nocapvote"
              target="_blank"
              rel="noopener noreferrer"
              icon={<YoutubeOutlined style={{ fontSize: 14 }} />}
            />
          </div>
        </Space>
      </Drawer>

      <Col xs={0} sm={0} md={0} lg={{ span: 12, order: 3 }} align="center">
        <Space size="large">
          {menuLinks} {accountLinks}
        </Space>
      </Col>
    </Row>
  );
};

export default TopNavBar;
